import { useEffect, useState } from 'react';

import palette from '@lib/core/src/theme/palette';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, LinearProgress, Stack, styled, Typography } from '@mui/material';

import { TaskDto, TaskDtoInvalidityReason } from '@/api/generated';
import useTasks from '@/hooks/useTasks';

export const Title = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '16px',
  color: alpha(palette.white, 0.7)
}));

export const TaskTitle = styled(Typography)(() => ({
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: '16px',
  color: palette.white
}));

export const ActionButton = styled(Stack)(() => ({
  borderRadius: '32px',
  gap: '8px',
  padding: '9px 16px',
  cursor: 'pointer',
  alignItems: 'center',
  backgroundColor: alpha(palette.white, 0.1),
  border: `1px solid ${palette.black}`,
  fontSize: '11px',
  fontWeight: 500,
  lineHeight: '14px',
  color: palette.white,
  ':hover': {
    border: `1px solid ${palette.white}`,
    backgroundColor: alpha(palette.white, 0.3)
  }
}));

const TaskDismissNotification = ({
  task,
  onClose,
  duration
}: {
  task: TaskDto;
  onClose: () => void;
  duration: number;
}) => {
  const { patchTask } = useTasks();
  const [progress, setProgress] = useState<number>(100);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => Math.max(prev - 100 / (duration / 100), 0));
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [duration]);

  const updateReason = (reason: TaskDtoInvalidityReason) => {
    patchTask(task.id!, {
      invalidityReason: reason
    }).then(() => onClose());
  };

  return (
    <Stack gap="16px">
      <Stack gap="6px">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Title>Help us improve your tasks - What’s wrong with this one?</Title>

          <CloseIcon
            onClick={onClose}
            sx={{ cursor: 'pointer', color: alpha(palette.white, 0.7), width: '18px' }}
          />
        </Stack>

        <TaskTitle>{task.title}</TaskTitle>
      </Stack>

      <Stack direction="row" alignItems="center" gap="8px">
        <ActionButton onClick={() => updateReason(TaskDtoInvalidityReason.notRelevant)}>
          Not relevant
        </ActionButton>
        <ActionButton onClick={() => updateReason(TaskDtoInvalidityReason.alreadyCompleted)}>
          Already completed
        </ActionButton>
        <ActionButton onClick={() => updateReason(TaskDtoInvalidityReason.duplicated)}>
          Duplicate of another task
        </ActionButton>
        <ActionButton onClick={() => updateReason(TaskDtoInvalidityReason.unclear)}>
          Unclear
        </ActionButton>
      </Stack>

      <LinearProgress
        sx={{
          background: palette.black,
          '& .MuiLinearProgress-bar': {
            backgroundColor: alpha(palette.white, 0.4)
          }
        }}
        variant="determinate"
        value={progress}
        style={{ height: '3px' }}
      />
    </Stack>
  );
};

export default TaskDismissNotification;
